import React, { useEffect, useState, useContext, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
	ContentContainer,
	ContentGroup,
	ListItem,
	QuestionDialog,
	Tariffable,
	createDialog
} from "coaz-ui";
import {
	PiBookFill,
	PiHashLight,
	PiIdentificationCardLight,
	PiStarLight,
	PiGraduationCapLight,
	PiCalendarLight,
	PiCalendarCheckLight,
	PiLockOpenLight,
	PiTextAlignLeft,
	PiTextAlignLeftLight,
	PiPencilSimple,
	PiTrash,
} from "react-icons/pi";
import { GlobalContext } from "../contexts/GlobalContext";
import client from "../client";
import EditCpd from "./EditCpd";
import AddTopic from "./AddTopic";
import EditTopic from "./EditTopic";
// import UserItems from "./UserItems";

const formatDay = (value) => {
	if (!value) return "";
	const date = new Date(value);
	if (isNaN(date)) return "";
	return date.toLocaleDateString("en-GB", {
		day: "numeric",
		month: "long",
		year: "numeric",
	});
};

const Cpd = () => {
	const { version, setVersion } = useContext(GlobalContext);

	const [cpd, setCpd] = useState(null);
	const [topics, setTopics] = useState([]);
	const [current, setCurrent] = useState(null);
	const [details, setDetails] = useState([]);
	const [buttons, setButtons] = useState([]);
	const [loading, setLoading] = useState(true);
	const versionRef = useRef(null);

	const setVersionRef = (value) => {
		setVersion(value);
	};

	useEffect(() => {
		versionRef.current = version;
	},[version])

	const { cpdId } = useParams();

	const { request, getCurrent } = client;

	const onAddTopic = (e) => {
		e.preventDefault();
		createDialog({
			title: "Add Topic",
			Component: AddTopic,
			params: { cpdId: cpdId },
			version: versionRef.current,
			setVersion: setVersionRef,
		});
	};

	const onEditTopic = (item) => {
		createDialog({
			title: "Edit Topic",
			Component: EditTopic,
			params: { id: item.id },
			version: versionRef.current,
			setVersion: setVersionRef,
		});
	};

	const onDeleteTopic = (item) => {
		createDialog({
			title: "Delete Topic",
			submitText: "Yes",
			cancelText: "No",
			Component: QuestionDialog,
			params: {
				message: `Are you sure you want to delete ${item.text}?`,
				submit: async () => {
					try {
						await request(
							"DELETE",`topics/${item.id}`,
						);
						return { message: "Successful", success: true };
					} catch (error) {
						return { message: client.errorMessage(error) };
					}
				},
			},
			version: versionRef.current,
			setVersion: setVersionRef,
		});
	};

	let topicButtons = [
		{ Icon: PiPencilSimple, name: "Edit", handler: onEditTopic },
		{ Icon: PiTrash, name: "Delete", handler: onDeleteTopic },
	];

	/* if (!current?.authorities?.find((auth) => auth.authority === "CPD:UPDATE")) topicButtons = undefined; */

	const getCpd = async () => {
		if (!cpdId) return;

		const curr = await getCurrent();
		setCurrent(curr);

		try {
			const response = await request("GET", `cpds/${cpdId}`);

			if (response && response.ref) {
				setCpd(response);

				const rows = [];
				if (response.ref?.id)
					rows.push({
						Icon: PiHashLight,
						lable: "Id",
						value: response.ref.id,
					});
				if (response.externalId) {
					rows.push({
						Icon: PiIdentificationCardLight,
						lable: "External Id",
						value: response.externalId,
					});
				}
				if (response.points)
					rows.push({
						Icon: PiStarLight,
						lable: "Points",
						value: response.points,
					});
				if (response.program?.name) {
					rows.push({
						Icon: PiGraduationCapLight,
						lable: "Program",
						value: response.program.name,
					});
				}
				if (response.availableFrom) {
					rows.push({
						Icon: PiCalendarLight,
						lable: "Available from",
						value: formatDay(response.availableFrom),
					});
				}
				if (response.availableTo) {
					rows.push({
						Icon: PiCalendarCheckLight,
						lable: "Available upto",
						value: formatDay(response.availableTo),
					});
				}
				rows.push({
					Icon: PiLockOpenLight,
					lable: "Available",
					value: response.available ? "Yes" : "No",
				});
				setDetails(rows);

				setButtons(
					[
						{
							Icon: PiTextAlignLeft,
							name: "Add Topic",
							handler: onAddTopic,
						},
					]
				);
			} else {
				setCpd(null);
			}
		} catch (error) {
			console.log(error);
			setCpd(null);
		}
	};
	const getTopics = async () => {
		try {
			const response = await request("GET","topics/by-cpd",null, {
				cpdId: cpdId
			});

			if(response && response.length > 0) {
				setTopics(response);
			} else {
				setTopics([])
			}
			
		} catch (error) {
			console.log(error);
			setTopics([]);
		}
	};

	/* const getTeachers = async () => {
     if (!SHOW_TEACHERS) return;
     try {
       const response = await requestFirst(CPD_TEACHERS_ENDPOINTS, {
         cpdId: courseId,
         courseId: courseId,
       });
       setTeachers(Array.isArray(response) ? response : response?.content ?? []);
     } catch (error) {
       setTeachers([]);
     }
   };

   const searchStudents = async (searchKey, page) => {
     try {
       return await requestFirst(CPD_STUDENTS_ENDPOINTS, {
         cpdId: courseId,
         courseId: courseId,
         searchKey: searchKey,
         pageNo: page.pageNo,
         pageSize: page.pageSize,
       });
     } catch (error) {
       console.log(error);
       return null;
     }
   };
 */
	const load = async () => {
		setLoading(true);
		await getCpd();
		await getTopics();
		// await getTeachers();
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, [cpdId, version]);

	const addButtons = (updater) => {
		setButtons((previous) => {
			const next =
				typeof updater === "function" ? updater(previous) : updater;
			const seen = new Set();
			return next.filter(
				(button) => !seen.has(button.name) && seen.add(button.name),
			);
		});
	};

	return (
		<Tariffable
			tariffable={cpd}
			current={
				current
					? { ...current, authorities: current.authorities ?? [] }
					: null
			}
			request={request}
			errorMessage={client.errorMessage}
			EditTariffable={EditCpd}
			version={version}
			setVersion={setVersion}
			buttons={buttons}
			setButtons={addButtons}
			details={details}
			loading={loading}
			setLoading={setLoading}
		>
			{topics.length > 0 && (
				<ContentGroup name="Topics">
					{topics.map((topic, i) => (
						<ListItem
							key={topic.ref?.id ?? topic.id ?? i}
							Icon={PiTextAlignLeftLight}
							item={{
								id: topic.ref?.id ?? topic.id,
								text: topic.name,
							}}
							buttons={topicButtons}
						/>
					))}
				</ContentGroup>
			)}

			{/* {SHOW_TEACHERS && path.includes(PROGRAMS) && teachers.length > 0 && (
        <ContentGroup name="CPD Teachers">
          {teachers.map((item, i) => (
            <ListItem key={i} Icon={PiChalkboardTeacherLight} item={{ text: teacherName(item) }} />
          ))}
        </ContentGroup>
      )}

      {path.includes(PROGRAMS) && (
        <ContentGroup name="Students">
          <UserItems
            key={courseId}
            search={searchStudents}
            mapItem={(row) => row.programStudent?.student ?? row.student ?? row.user ?? row}
            version={version}
          />
        </ContentGroup>
      )} */}
		</Tariffable>
	);
};

export default Cpd;
